<script setup>
import {computed, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import Button from "@/components/forms/Button.vue";
import BondSettingsModal from "@/components/modals/BondSettingsModal.vue";
import BondDocumentsTab from "@/components/bonds/BondDocumentsTab.vue";
import BondService from "@/services/BondService";
import IntermediaryService from "@/services/IntermediaryService";
import {useDataTable} from "@/utils/datatableHelper";
import {formatAmount, formatDate, formatNumber, showSuccessMessage} from "@/helper";
import {riskToleranceCorrectionOptions} from "@/utils/targetMarketOptions";
import {useBondsPageGuard} from '@/utils/features';

/**
 * IMA-517: detail dluhopisu v administraci (menu Dluhopisy).
 *
 * Záložky jako ve vzoru (_Docs/IMA-517-dluhopisy-prototyp.html): Parametry (údaje z ESO, jen pro čtení),
 * Nastavení v aplikaci (aktivace, období prodeje, cílový trh, poplatek), Plán výplat (orientační na 1 ks),
 * Dokumenty k emisi s verzemi a Klienti.
 */
const {t, te} = useI18n();
// v instanci bez dluhopisů (B.I.G. Invest SK/CZ) stránka přesměruje na nástěnku
useBondsPageGuard();
const router = useRouter();
const bondId = router.currentRoute.value.params.id;
const bond = ref({});
const isLoading = ref(true);
const activeTab = ref('params');
const intermediaries = ref([]);

const saleStateBadge = {
  ON_SALE: 'bg-success',
  PREPARING: 'bg-primary',
  CLOSED: 'bg-secondary',
  UNKNOWN: 'bg-warning text-dark',
};

const clientsTable = useDataTable(fetchClients, 'signed_date', 10, false);
const clientColumns = ref([
  {label: t('bondsDetail.client'), field: 'client', sortable: false},
  {label: t('bondsDetail.intermediary_person'), field: 'intermediary_person', sortable: false},
  {label: t('bondsDetail.quantity'), field: 'quantity', type: 'number'},
  {label: t('bondsDetail.nominal'), field: 'amount', sortable: false},
  {label: t('bondsDetail.signed_date'), field: 'signed_date'},
  {label: t('bondsDetail.activation_date'), field: 'activation_date'},
  {label: t('bondsDetail.state'), field: 'state', sortable: false},
]);

onMounted(() => {
  fetchBond();
  IntermediaryService.getAll().then(response => {
    intermediaries.value = response;
  });
});

function fetchBond() {
  isLoading.value = true;
  BondService.getBond(bondId).then(response => {
    bond.value = response;
    isLoading.value = false;
  });
}

function fetchClients(body) {
  clientsTable.isLoading.value = true;
  BondService.fetchClients(bondId, body).then(response => {
    clientsTable.totalRecords.value = response.meta.total;
    clientsTable.rows.value = response.data;
    clientsTable.isLoading.value = false;
  });
}

function editSettings() {
  const {reveal, onConfirm} = createConfirmDialog(BondSettingsModal, {bond: bond.value, intermediaries: intermediaries.value});
  reveal();
  onConfirm((data) => {
    BondService.saveSettings(bondId, data).then(response => {
      showSuccessMessage(response.message);
      // uložení vrací jen detail bez plánu výplat – plán se nastavením nemění, ponecháme původní
      bond.value = {...response.bond, payment_schedule: bond.value.payment_schedule};
    });
  });
}

const formatDay = value => value ? formatDate(value, 'd.m.Y') : '---';

const isSet = value => value !== null && value !== undefined;
const volume = value => (isSet(value) ? formatAmount(value, bond.value.currency, 0) : null);

/** Volný objem = objem emise − upsaný objem; ESO ho neposílá, dopočítá se z obou údajů. */
const freeVolume = computed(() => (isSet(bond.value.emission_volume) && isSet(bond.value.subscribed_volume)
  ? bond.value.emission_volume - bond.value.subscribed_volume
  : null));

/**
 * Parametry z ESO v pořadí jako ve vzoru. Prázdná hodnota = ESO údaj zatím neposílá (štítek u popisku).
 */
const esoParams = computed(() => [
  [t('bondsDetail.name'), bond.value.name],
  [t('bondsDetail.isin'), bond.value.isin],
  [t('bondsDetail.issuer'), bond.value.issuer],
  [t('bondsDetail.currency'), bond.value.currency],
  [t('bondsDetail.coupon_rate'), isSet(bond.value.coupon_rate) ? formatNumber(bond.value.coupon_rate) + ' % p.a.' : null],
  [t('bondsDetail.coupon_frequency'), bond.value.coupon_frequency_label],
  [t('bondsDetail.nominal_value'), isSet(bond.value.nominal_value) ? formatAmount(bond.value.nominal_value, bond.value.currency) : null],
  [t('bondsDetail.issue_date'), bond.value.issue_date ? formatDay(bond.value.issue_date) : null],
  [t('bondsDetail.maturity_date'), bond.value.maturity_date ? formatDay(bond.value.maturity_date) : null],
  [t('bondsDetail.emission_volume'), volume(bond.value.emission_volume)],
  [t('bondsDetail.subscribed_volume'), volume(bond.value.subscribed_volume)],
  [t('bondsDetail.free_volume'), volume(freeVolume.value)],
]);

/** Orientační plán výplat na 1 ks z BE (ClientBondReportService::adminIndicativeSchedule). */
const schedule = computed(() => bond.value.payment_schedule ?? []);

// plán je celý v paměti – řazení a stránkování dělá tabulka sama (ne remote jako Klienti)
const scheduleColumns = ref([
  {label: t('bondsDetail.period'), field: 'period_from', sortable: false},
  {label: t('bondsDetail.payment_date'), field: 'payment_date'},
  {label: t('bondsDetail.amount_gross'), field: 'amount_gross', type: 'number'},
  {label: t('bondsDetail.amount_net'), field: 'amount_net', type: 'number'},
  {label: t('bondsDetail.payment_type'), field: 'type', sortable: false},
]);

/** Přeloží hodnoty cílového trhu stejně jako detail strategie (klíče createUpdateStrategyModal.*). */
function targetMarketLabel(values, prefix = '') { // prefix 'risk' = hodnoty korekce rizikové tolerance
  if (!values || values.length === 0) {
    return t('bondsDetail.no_restriction');
  }
  return values.map(value => {
    // korekce rizikové tolerance se ukládá jako číslo 1–7, překlad je u textového kódu (např. 3_BALANCED)
    const riskOption = riskToleranceCorrectionOptions.find(option => option.key === Number(value));
    const code = prefix === 'risk' && riskOption ? riskOption.value : value;
    const key = 'createUpdateStrategyModal.' + code;
    return te(key) ? t(key) : String(value);
  }).join(', ');
}

/** Názvy zprostředkovatelů, pro které je dluhopis dostupný; prázdné = všichni. */
const intermediariesLabel = computed(() => {
  const ids = bond.value.available_for_intermediaries;
  if (!ids || ids.length === 0) {
    return t('bondsDetail.all_intermediaries');
  }
  return ids.map(id => intermediaries.value.find(item => String(item.id) === String(id))?.intermediary_name ?? id).join(', ');
});

/** Měna poplatku – u procentního poplatku se pásma nákupu počítají v měně dluhopisu. */
const feeCurrency = computed(() => bond.value.fee?.currency ?? bond.value.currency ?? 'CZK');

/** Výše poplatku v pásmu: procenta nebo částka v měně poplatku. */
const feeValue = tier => (bond.value.fee.type === 'PERCENT' ? formatNumber(tier.value) + ' %' : formatAmount(tier.value, feeCurrency.value));
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.push({name: 'Securities'})" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('bondsDetail.back') }}</a>
        </div>
        <div class="col-6 text-center">
          <div class="page-section__title">{{ bond.name ?? t('bondsDetail.title') }}</div>
        </div>
        <div class="col-3 d-flex justify-content-end align-items-start gap-1">
          <span class="badge bg-secondary">{{ t('bondsDetail.bond_badge') }}</span>
          <span v-if="bond.sale_state" class="badge" :class="saleStateBadge[bond.sale_state]">{{ t('bonds.sale_states.' + bond.sale_state) }}</span>
        </div>
      </div>
    </div>

    <div v-if="!isLoading" class="page-section__content">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <a class="nav-link ps-2 pe-3" :class="{ active: activeTab === 'params' }" href="#" @click.prevent="activeTab = 'params'">{{ t('bondsDetail.tab_params') }}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link px-3" :class="{ active: activeTab === 'settings' }" href="#" @click.prevent="activeTab = 'settings'">{{ t('bondsDetail.tab_settings') }}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link px-3" :class="{ active: activeTab === 'schedule' }" href="#" @click.prevent="activeTab = 'schedule'">{{ t('bondsDetail.tab_schedule') }}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link px-3" :class="{ active: activeTab === 'documents' }" href="#" @click.prevent="activeTab = 'documents'">{{ t('bondsDetail.tab_documents') }}</a>
        </li>
        <li class="nav-item">
          <a class="nav-link px-3" :class="{ active: activeTab === 'clients' }" href="#" @click.prevent="activeTab = 'clients'">{{ t('bondsDetail.tab_clients') }}</a>
        </li>
      </ul>

      <!-- Parametry z ESO: jen pro čtení, přepisuje je noční import -->
      <div v-show="activeTab === 'params'" class="card">
        <div class="card-header">
          <div class="card-header__title">{{ t('bondsDetail.eso_params') }}</div>
          <div class="card-header__subtitle">{{ t('bondsDetail.eso_params_hint') }}</div>
        </div>
        <div class="card-body">
          <div class="row">
            <div v-for="(chunk, index) in [esoParams.slice(0, 6), esoParams.slice(6)]" :key="index" class="col-12 col-md-6 info-table">
              <div v-for="[label, value] in chunk" :key="label" class="info-table__row row">
                <span class="info-table__label col-12">
                  {{ label }}
                  <!-- štítek jako ve vzoru: údaj z ESO, nebo údaj, který ESO zatím neposílá -->
                  <span v-if="value === null || value === undefined" class="badge bg-warning text-dark fw-normal ms-1">{{ t('bondsDetail.eso_missing') }}</span>
                  <span v-else class="badge bg-secondary fw-normal ms-1">{{ t('bondsDetail.eso_badge') }}</span>
                </span>
                <span class="info-table__value col-12">{{ value ?? '---' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Nastavení v aplikaci: spravuje administrátor, import ho nepřepisuje -->
      <div v-show="activeTab === 'settings'" class="card">
        <div class="card-header">
          <div class="row">
            <div class="col">
              <div class="card-header__title">{{ t('bondsDetail.app_settings') }}</div>
            </div>
            <div class="col text-end">
              <Button :label="t('bondsDetail.edit')" @click="editSettings()"/>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6 info-table">
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondsDetail.is_active') }}</span>
                <span class="info-table__value col-12">{{ bond.is_active ? t('strategies.yes') : t('strategies.no') }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondsDetail.sale_period') }}</span>
                <span class="info-table__value col-12">{{ bond.sale_from || bond.sale_to ? formatDay(bond.sale_from) + ' – ' + formatDay(bond.sale_to) : t('bondsDetail.sale_period_missing') }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondsDetail.intermediaries') }}</span>
                <span class="info-table__value col-12">{{ intermediariesLabel }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondsDetail.client_description') }}</span>
                <span class="info-table__value col-12">{{ bond.client_description ?? '---' }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondSettingsModal.purchase_order_template') }}</span>
                <span class="info-table__value col-12">{{ bond.purchase_order_template_name ?? t('bondSettingsModal.purchase_order_template_none') }}</span>
              </div>
              <!-- min./max. počet kusů na nákup – bez vyplnění min. 1 ks a bez maxima -->
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondSettingsModal.min_quantity') }}</span>
                <span class="info-table__value col-12">{{ bond.min_quantity ?? t('bondSettingsModal.min_quantity_default') }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondSettingsModal.max_quantity') }}</span>
                <span class="info-table__value col-12">{{ bond.max_quantity ?? t('bondsDetail.no_restriction') }}</span>
              </div>
            </div>
            <div class="col-12 col-md-6 info-table">
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('bondsDetail.risk_profile') }}</span>
                <span class="info-table__value col-12">{{ bond.risk_profile ?? t('bondsDetail.no_restriction') }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.client_type') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.client_type) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.adequacy_test') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.adequacy_test) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.ability_to_bear_loss') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.ability_to_bear_loss) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.risk_tolerance_correction') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.risk_tolerance_correction, 'risk') }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.investment_horizon') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.investment_horizon) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.client_investment_goals') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.client_investment_goals) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.investment_preferences') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.investment_preferences) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-12">{{ t('createUpdateStrategyModal.client_esg') }}</span>
                <span class="info-table__value col-12">{{ targetMarketLabel(bond.client_esg) }}</span>
              </div>
            </div>
          </div>

          <!-- Vstupní poplatek jako ve vzoru: typ, měna a pásma podle výše nákupu -->
          <h6 class="mt-4 mb-1">{{ t('bondsDetail.fee') }}</h6>
          <p class="small text-muted mb-2">{{ t('bondsDetail.fee_evidence_only') }}</p>
          <template v-if="bond.fee && bond.fee.tiers">
            <p class="mb-2">
              {{ t('bondSettingsModal.fee_type') }}: <strong>{{ bond.fee.type === 'PERCENT' ? t('bondSettingsModal.fee_type_percent') : t('bondSettingsModal.fee_type_fixed') }}</strong>,
              {{ t('bondSettingsModal.currency') }}: <strong>{{ feeCurrency }}</strong>
            </p>
            <table class="table">
              <thead>
              <tr>
                <th scope="col" class="text-end">{{ t('bondSettingsModal.amount_from') }}</th>
                <th scope="col" class="text-end">{{ t('bondSettingsModal.amount_to') }}</th>
                <th scope="col" class="text-end">{{ t('bondSettingsModal.value_fixed') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(tier, index) in bond.fee.tiers" :key="index">
                <td class="text-end">{{ formatAmount(tier.amount_from, feeCurrency) }}</td>
                <td class="text-end">{{ tier.amount_to === null || tier.amount_to === undefined ? t('bondsDetail.and_more') : formatAmount(tier.amount_to, feeCurrency) }}</td>
                <td class="text-end">{{ feeValue(tier) }}</td>
              </tr>
              </tbody>
            </table>
          </template>
          <p v-else>{{ t('bondsDetail.no_fee') }}</p>
        </div>
      </div>

      <!-- Plán výplat jako ve vzoru: poznámka a tabulka (vue-good-table se stránkováním), orientační na 1 ks
      z parametrů dluhopisu – ESO posílá plán jen za jednotlivé klienty -->
      <div v-show="activeTab === 'schedule'">
        <p class="small text-muted">{{ t('bondsDetail.schedule_hint') }}</p>
        <vue-good-table
            :columns="scheduleColumns"
            :rows="schedule"
            :row-style-class="row => (row.is_paid ? 'text-muted' : '')"
            :pagination-options="{
                  enabled: true,
                  perPage: 10,
                  perPageDropdown: [10, 20],
                  dropdownAllowAll: false,
                  rowsPerPageLabel: t('strategies.entriesPerPage'),
                  nextLabel: t('strategies.next'),
                  prevLabel: t('strategies.previous'),
                  ofLabel: t('strategies.of'),
                }">
          <template #table-row="props">
            <span v-if="props.column.field === 'period_from'">{{ formatDay(props.row.period_from) }} – {{ formatDay(props.row.period_to) }}</span>
            <span v-else-if="props.column.field === 'payment_date'">{{ formatDay(props.row.payment_date) }}</span>
            <span v-else-if="props.column.field === 'amount_gross'">{{ formatAmount(props.row.amount_gross, props.row.currency) }}</span>
            <span v-else-if="props.column.field === 'amount_net'">{{ formatAmount(props.row.amount_net, props.row.currency) }}</span>
            <span v-else-if="props.column.field === 'type'">{{ t('bondsDetail.payment_types.' + props.row.type) }}</span>
            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
          </template>
          <template v-slot:emptystate>
            <div class="text-center">{{ t('bondsDetail.no_schedule') }}</div>
          </template>
        </vue-good-table>
      </div>

      <!-- Dokumenty k emisi s verzemi; v-if: načítají se až při otevření záložky -->
      <div v-if="activeTab === 'documents'">
        <BondDocumentsTab :bond-id="bondId"/>
      </div>

      <!-- Klienti, kteří dluhopis vlastní -->
      <div v-show="activeTab === 'clients'">
        <vue-good-table
            :columns="clientColumns"
            :rows="clientsTable.rows.value"
            mode="remote"
            @page-change="clientsTable.onPageChange"
            @sort-change="clientsTable.onSortChange"
            @per-page-change="clientsTable.onPerPageChange"
            :totalRows="clientsTable.totalRecords.value"
            :pagination-options="{
                  enabled: true,
                  rowsPerPageLabel: t('strategies.entriesPerPage'),
                  nextLabel: t('strategies.next'),
                  prevLabel: t('strategies.previous'),
                  ofLabel: t('strategies.of'),
                }">
          <template #table-row="props">
            <span v-if="props.column.field === 'client'">{{ props.row.client?.full_name ?? '---' }}</span>
            <span v-else-if="props.column.field === 'intermediary_person'">{{ props.row.client?.intermediary_person ?? '---' }}</span>
            <!-- nominál = kusy × nominální hodnota (BE nominal_amount) – i u nákupu z aplikace, kterému ESO ještě nevrátilo částku -->
            <span v-else-if="props.column.field === 'amount'">{{ (props.row.nominal_amount ?? props.row.amount) === null ? '---' : formatAmount(props.row.nominal_amount ?? props.row.amount, bond.currency) }}</span>
            <span v-else-if="props.column.field === 'signed_date'">{{ formatDay(props.row.signed_date) }}</span>
            <span v-else-if="props.column.field === 'activation_date'">{{ formatDay(props.row.activation_date) }}</span>
            <span v-else-if="props.column.field === 'state'">{{ t('bondsDetail.client_bond_states.' + props.row.state) }}</span>
            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
          </template>
          <template v-slot:emptystate>
            <div class="text-center">{{ clientsTable.isLoading.value ? t('strategies.data_loading') : t('bondsDetail.no_clients') }}</div>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* záložky jako v IntermediaryPeopleDetail.vue: jen podtržení aktivní záložky */
:deep(.nav-tabs) {
  border-bottom: none;
}
:deep(.nav-tabs .nav-link) {
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--bs-body-color);
  padding-bottom: 0.75rem;
}
:deep(.nav-tabs .nav-link.active) {
  border-bottom-color: var(--primaryColor);
  color: var(--primaryColor);
  background: transparent;
  font-weight: 600;
}
:deep(.nav-tabs .nav-link:hover:not(.active)) {
  border-bottom-color: #dee2e6;
}
</style>
